<template>
  <div class="user-manage">
    <div class="page-header">
      <h2>用户管理</h2>
      <p>管理系统用户，查看用户学习情况和统计数据</p>
    </div>

    <el-card class="filter-card">
      <div class="filter-content">
        <div class="filter-left">
          <el-input
            v-model="filter.keyword"
            placeholder="请输入用户名或手机号"
            prefix-icon="el-icon-search"
            style="width: 300px;"
            clearable
          />
        </div>
        <div class="filter-actions">
          <el-button type="primary" icon="el-icon-plus" @click="handleAdd">
            新增用户
          </el-button>
          <el-button type="info" icon="el-icon-refresh" @click="refresh">
            刷新
          </el-button>
        </div>
      </div>
    </el-card>

    <el-card>
      <el-table :data="filteredUsers" border style="width: 100%" v-loading="loading">
        <el-table-column prop="id" label="ID" width="80" align="center"></el-table-column>
        <el-table-column label="用户信息" min-width="200">
          <template slot-scope="scope">
            <div class="user-info-cell">
              <el-avatar :src="scope.row.avatar" size="medium"></el-avatar>
              <div class="user-info-content">
                <div class="user-name">{{ scope.row.name }}</div>
                <div class="user-real-name" v-if="scope.row.real_name">姓名：{{ scope.row.real_name }}</div>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="身份证号" width="180">
          <template slot-scope="scope">
            <span v-if="scope.row.id_card">{{ scope.row.id_card }}</span>
            <span v-else class="no-data">-</span>
          </template>
        </el-table-column>
        <el-table-column label="学习统计" width="180">
          <template slot-scope="scope">
            <div class="study-stats">
              <div class="stat-item">
                <span class="stat-label">学习数：</span>
                <span class="stat-value">{{ scope.row.studyCount }}</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">完成数：</span>
                <span class="stat-value">{{ scope.row.completedCount }}</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">正确率：</span>
                <el-tag
                  :type="getRateType(scope.row.correctRate)"
                  size="mini"
                  style="margin-left: 4px;"
                >
                  {{ scope.row.correctRate }}%
                </el-tag>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="lastLogin" label="最后登录" width="150"></el-table-column>
        <el-table-column label="操作" width="200" align="center" fixed="right">
          <template slot-scope="scope">
            <el-button
              type="primary"
              icon="el-icon-view"
              size="mini"
              @click="handleViewDetail(scope.row)"
            >详情</el-button>
            <el-button
              type="warning"
              icon="el-icon-edit"
              size="mini"
              @click="handleEdit(scope.row)"
            >编辑</el-button>
            <el-button
              type="danger"
              icon="el-icon-delete"
              size="mini"
              @click="handleDelete(scope.row.id)"
            >删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 用户详情对话框 -->
    <el-dialog
      title="用户详情"
      :visible.sync="detailDialogVisible"
      width="600px"
    >
      <div v-if="currentUser" class="user-detail">
        <div class="detail-header">
          <el-avatar :src="currentUser.avatar" :size="80"></el-avatar>
          <div class="detail-header-info">
            <h3>{{ currentUser.name }}</h3>
            <p v-if="currentUser.real_name">姓名：{{ currentUser.real_name }}</p>
            <p v-if="currentUser.id_card">身份证号：{{ currentUser.id_card }}</p>
            <p>最后登录：{{ currentUser.lastLogin }}</p>
          </div>
        </div>
        
        <el-divider></el-divider>
        
        <div class="detail-stats">
          <h4>学习统计数据</h4>
          <el-row :gutter="20">
            <el-col :span="8">
              <div class="stat-card">
                <div class="stat-title">总学习数</div>
                <div class="stat-number">{{ currentUser.studyCount }}</div>
              </div>
            </el-col>
            <el-col :span="8">
              <div class="stat-card">
                <div class="stat-title">完成数</div>
                <div class="stat-number">{{ currentUser.completedCount }}</div>
              </div>
            </el-col>
            <el-col :span="8">
              <div class="stat-card">
                <div class="stat-title">正确率</div>
                <div class="stat-number">{{ currentUser.correctRate }}%</div>
              </div>
            </el-col>
          </el-row>
        </div>
        
        <div class="detail-records">
          <h4>最近学习记录</h4>
          <el-table :data="userLearningRecords" border size="mini">
            <el-table-column prop="videoTitle" label="课程" min-width="150"></el-table-column>
            <el-table-column label="状态" width="80">
              <template slot-scope="scope">
                <el-tag
                  :type="scope.row.completed ? 'success' : 'warning'"
                  size="mini"
                >
                  {{ scope.row.completed ? '已完成' : '学习中' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="答题结果" width="80">
              <template slot-scope="scope">
                <el-tag
                  v-if="scope.row.quizCorrect !== null"
                  :type="scope.row.quizCorrect ? 'success' : 'danger'"
                  size="mini"
                >
                  {{ scope.row.quizCorrect ? '正确' : '错误' }}
                </el-tag>
                <span v-else>-</span>
              </template>
            </el-table-column>
            <el-table-column prop="completeTime" label="完成时间" width="150"></el-table-column>
          </el-table>
        </div>
      </div>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="detailDialogVisible = false">关闭</el-button>
      </div>
    </el-dialog>

    <!-- 新增/编辑对话框 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="500px"
      @close="resetForm"
    >
      <el-form
        ref="userForm"
        :model="formData"
        :rules="formRules"
        label-width="80px"
      >
        <el-form-item label="用户名" prop="name">
          <el-input
            v-model="formData.name"
            placeholder="请输入用户名"
            maxlength="20"
            show-word-limit
          />
        </el-form-item>
        
        <el-form-item label="姓名">
          <el-input
            v-model="formData.real_name"
            placeholder="请输入真实姓名（可选）"
            maxlength="20"
          />
        </el-form-item>
        
        <el-form-item label="身份证号">
          <el-input
            v-model="formData.id_card"
            placeholder="请输入身份证号（可选）"
            maxlength="18"
          />
        </el-form-item>
        
        <el-form-item label="密码" prop="password">
          <el-input
            v-model="formData.password"
            type="password"
            placeholder="请输入密码"
            show-password
          />
        </el-form-item>
      </el-form>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitForm" :loading="submitting">
          确认
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'

export default {
  name: 'UserManagement',
  data() {
    return {
      loading: false,
      dialogVisible: false,
      detailDialogVisible: false,
      submitting: false,
      isEdit: false,
      currentUser: null,
      currentId: null,
      filter: {
        keyword: ''
      },
      formData: {
        name: '',
        password: '',
        real_name: '',
        id_card: '',
        avatar: 'https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png'
      },
      formRules: {
        name: [
          { required: true, message: '请输入用户名', trigger: 'blur' }
        ],
        password: [
          { required: true, message: '请输入密码', trigger: 'blur' },
          { min: 6, message: '密码长度不能少于6位', trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    ...mapState(['users', 'studyRecords']),
    filteredUsers() {
      if (!this.filter.keyword) {
        return this.users
      }
      const keyword = this.filter.keyword.toLowerCase()
      return this.users.filter(user => 
        user.name.toLowerCase().includes(keyword) ||
        (user.real_name && user.real_name.toLowerCase().includes(keyword)) ||
        (user.id_card && user.id_card.includes(keyword))
      )
    },
    dialogTitle() {
      return this.isEdit ? '编辑用户' : '新增用户'
    },
    userLearningRecords() {
      if (!this.currentUser) return []
      return this.studyRecords
        .filter(record => record.userId === this.currentUser.id)
        .slice(0, 5) // 只显示最近5条
    }
  },
  async created() {
    await this.loadData()
  },
  methods: {
    ...mapActions(['addUser', 'updateUser', 'deleteUser', 'fetchUsers']),
    async loadData() {
      this.loading = true
      try {
        await this.fetchUsers()
      } catch (error) {
        this.$message.error('加载用户列表失败：' + error.message)
      } finally {
        this.loading = false
      }
    },
    getRateType(rate) {
      if (rate >= 80) return 'success'
      if (rate >= 60) return 'warning'
      return 'danger'
    },
    async refresh() {
      await this.loadData()
      this.$message.success('刷新成功')
    },
    handleAdd() {
      this.isEdit = false
      this.dialogVisible = true
    },
    handleEdit(row) {
      this.isEdit = true
      this.currentId = row.id
      this.formData = {
        name: row.name,
        password: '********',
        real_name: row.real_name || '',
        id_card: row.id_card || '',
        avatar: row.avatar
      }
      this.dialogVisible = true
    },
    handleViewDetail(row) {
      this.currentUser = row
      this.detailDialogVisible = true
    },
    async handleDelete(id) {
      try {
        await this.$confirm('确定要删除这个用户吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        await this.deleteUser(id)
        this.$message.success('删除成功')
      } catch {
        // 用户取消了删除
      }
    },
    submitForm() {
      this.$refs.userForm.validate(async valid => {
        if (valid) {
          this.submitting = true
          
          try {
            if (this.isEdit) {
              // 编辑用户
              const updateData = {
                name: this.formData.name,
                real_name: this.formData.real_name,
                id_card: this.formData.id_card
              }
              
              // 如果密码不是占位符，则更新密码
              if (this.formData.password !== '********') {
                updateData.password = this.formData.password
              }
              
              await this.updateUser({
                id: this.currentId,
                user: updateData
              })
              this.$message.success('更新成功')
            } else {
              // 新增用户
              const userData = {
                name: this.formData.name,
                real_name: this.formData.real_name,
                id_card: this.formData.id_card,
                avatar: this.formData.avatar,
                password: this.formData.password,
                studyCount: 0,
                completedCount: 0,
                correctRate: 0,
                lastLogin: new Date().toISOString().split('T')[0] + ' ' + 
                           new Date().toTimeString().split(' ')[0].slice(0, 5)
              }
              
              await this.addUser(userData)
              this.$message.success('新增成功')
            }
            
            this.dialogVisible = false
            this.resetForm()
          } finally {
            this.submitting = false
          }
        }
      })
    },
    resetForm() {
      if (this.$refs.userForm) {
        this.$refs.userForm.resetFields()
      }
      this.isEdit = false
      this.currentId = null
      this.formData = {
        name: '',
        password: '',
        real_name: '',
        id_card: '',
        avatar: 'https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png'
      }
    }
  }
}
</script>

<style scoped>
.user-manage {
  padding: 20px;
}

.page-header {
  margin-bottom: 24px;
}

.page-header h2 {
  margin: 0 0 8px 0;
  font-size: 24px;
  color: #333;
}

.page-header p {
  margin: 0;
  color: #999;
  font-size: 14px;
}

.filter-card {
  margin-bottom: 20px;
}

.filter-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.filter-left {
  display: flex;
  gap: 10px;
}

.filter-actions {
  display: flex;
  gap: 10px;
}

.user-info-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-info-content {
  display: flex;
  flex-direction: column;
}

.user-name {
  font-weight: 500;
  color: #333;
  margin-bottom: 4px;
}

.user-real-name {
  color: #666;
  font-size: 13px;
}

.no-data {
  color: #999;
}

.study-stats {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.stat-item {
  display: flex;
  align-items: center;
  font-size: 13px;
}

.stat-label {
  color: #666;
  min-width: 50px;
}

.stat-value {
  font-weight: 500;
  color: #333;
}

.user-detail {
  padding: 10px 0;
}

.detail-header {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 20px;
}

.detail-header-info h3 {
  margin: 0 0 8px 0;
  font-size: 20px;
  color: #333;
}

.detail-header-info p {
  margin: 4px 0;
  color: #666;
  font-size: 14px;
}

.detail-stats {
  margin: 20px 0;
}

.detail-stats h4 {
  margin: 0 0 16px 0;
  color: #333;
}

.stat-card {
  text-align: center;
  padding: 16px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background-color: #fafafa;
}

.stat-title {
  color: #666;
  font-size: 13px;
  margin-bottom: 8px;
}

.stat-number {
  font-size: 24px;
  font-weight: bold;
  color: #333;
}

.detail-records {
  margin-top: 20px;
}

.detail-records h4 {
  margin: 0 0 16px 0;
  color: #333;
}

.form-tip {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
}
</style>